import { CompositeLayer } from '@deck.gl/core';
import { ScatterplotLayer } from '@deck.gl/layers';

/** @param {object} d */
function threatRowLonLat(d) {
  const lon = d.lon ?? d.longitude;
  const lat = d.lat ?? d.latitude;
  if (lat == null || lon == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) return null;
  return [Number(lon), Number(lat)];
}

class PulsingThreatLayer extends CompositeLayer {
  static layerName = 'PulsingThreatLayer';

  static defaultProps = {
    data: [],
    visible: true,
    pulsing: true,
    /** 'geographic' = meter rings (map-wide); 'screen' = pixel rings at vessel heads (Vessel Tracking). */
    radiusMode: 'geographic',
    currentTime: 0,
    /** Screen mode only: stroke-only pulse (no red disk fill) — e.g. exercise Op Alpha proximity head. */
    ringOnly: false
  };

  initializeState() {
    this.state = {};
  }

  shouldUpdateState({ changeFlags }) {
    return changeFlags.somethingChanged;
  }

  renderLayers() {
    if (!this.props.visible) return [];

    const { data, pulsing, radiusMode, ringOnly } = this.props;
    const now = Date.now();
    const screenMode = radiusMode === 'screen';

    if (pulsing) {
      this.setNeedsRedraw(true);
    }

    const threatData = data.filter(d => {
      const isThreat = d.threat_detected === 'Yes' || d.threat_detected === true;
      return isThreat && threatRowLonLat(d) != null;
    });

    if (threatData.length === 0) return [];

    const cycleDuration = screenMode ? 1600 : 1500;

    return [
      new ScatterplotLayer({
        id: `${this.props.id}-circles`,
        data: threatData,
        getPosition: d => threatRowLonLat(d),
        radiusUnits: screenMode ? 'pixels' : 'meters',

        getRadius: (d, { index }) => {
          if (screenMode) {
            if (ringOnly) {
              if (!pulsing) return 16;
              const offset = index * 0.12 * cycleDuration;
              const t = ((now + offset) % cycleDuration) / cycleDuration;
              return 10 + t * 12;
            }
            if (!pulsing) return 24;
            const offset = index * 0.12 * cycleDuration;
            const t = ((now + offset) % cycleDuration) / cycleDuration;
            return 12 + t * 30;
          }
          if (!pulsing) return 2000;
          const offset = index * 0.15 * cycleDuration;
          const t = ((now + offset) % cycleDuration) / cycleDuration;
          const scale = 1 + t * 2;
          return 1000 * scale;
        },

        getFillColor: (d, { index }) => {
          if (screenMode) {
            if (ringOnly) return [0, 0, 0, 0];
            if (!pulsing) return [239, 68, 68, 100];
            const offset = index * 0.12 * cycleDuration;
            const t = ((now + offset) % cycleDuration) / cycleDuration;
            const alpha = Math.round(110 * (1 - t));
            return [239, 68, 68, alpha];
          }
          if (!pulsing) return [239, 68, 68, 200];
          const offset = index * 0.15 * cycleDuration;
          const t = ((now + offset) % cycleDuration) / cycleDuration;
          const alpha = Math.round(150 * (1 - t));
          return [239, 68, 68, alpha];
        },

        stroked: true,
        lineWidthUnits: screenMode ? 'pixels' : 'meters',
        getLineColor: [220, 38, 38, screenMode ? 230 : 255],
        getLineWidth: screenMode ? 1.5 : 2,
        ...(screenMode
          ? {}
          : {
              lineWidthMinPixels: 2,
              lineWidthMaxPixels: 4
            }),

        pickable: false,
        ...(!screenMode
          ? {
              radiusMinPixels: 6,
              radiusMaxPixels: 50
            }
          : {}),

        updateTriggers: {
          getRadius: [pulsing, now, radiusMode, ringOnly],
          getFillColor: [pulsing, now, radiusMode, ringOnly]
        }
      })
    ];
  }
}

export default PulsingThreatLayer;
